import React, { Component } from "react";
import { addMilliseconds, subSeconds } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ICamera } from "@formant/core/model/ICamera";
import { IStreamData } from "@formant/core/model/IStreamData";
import { ITransformTreeViewConfiguration } from "@formant/core/model/ITransformTreeViewConfiguration";
import { Uuid } from "@formant/core/model/Uuid";

import { observeStore, queryStore } from "../../stores";
import { freeWebglContext, getWebglContext } from "../../utils/webglCheck";
import { DeviceSelector } from "../common/DeviceSelector";
import { DeviceTags } from "../common/DeviceTags";
import { WebglError } from "../common/WebglError";
import { Lazy } from "../Lazy";
import { IBaseModuleProps } from "./IBaseModuleProps";
import { Module } from "./Module";
import { ModuleFooter } from "./ModuleFooter";
import * as styles from "./TransformTreeModule.scss";
import { findConfiguration } from "./utils/findConfiguration";
import { pointsByDevice } from "./utils/pointsByDevice";

const ViewerView = Lazy(async () => (await import("./ViewerView")).ViewerView);

@observer
export class TransformTreeModule extends Component<IBaseModuleProps> {
    @observable
    private selectedDeviceId?: Uuid;
    private camera?: ICamera;
    private data: IStreamData<"transform tree">[] = [];
    private hasWebglContext = false;

    public componentDidMount() {
        this.hasWebglContext = getWebglContext();

        const { configuration, streamName } = this.props;
        const { camera } =
            findConfiguration(configuration, streamName, "transform tree") ||
            {};
        this.camera = camera;
    }

    public componentWillUnmount() {
        if (this.hasWebglContext) {
            freeWebglContext();
        }
    }

    public render() {
        const { filter, streamName, focused, hovering, fullscreen } =
            this.props;
        const { selectedDeviceId } = this;
        const { time } = observeStore;

        const startTime = subSeconds(time, 10);
        const endTime = addMilliseconds(time, 1);

        const queryData = queryStore.moduleQuery(
            filter,
            streamName,
            "transform tree",
            startTime,
            endTime,
        );

        const loading = queryData === undefined;
        const tooMuchData = queryData === "too much data";

        this.data = (queryData !== "too much data" && queryData) || this.data;
        const data = this.data;

        const empty = data.length === 0;

        const datas = pointsByDevice(data);

        const active = datas.find(
            ({ deviceId }) => selectedDeviceId === deviceId,
        );

        const deviceIds = data.map(_ => _.deviceId);

        const current = active?.points?.slice(-1)?.[0];

        const nodes = active?.points.map(_ => _.value) || [];

        return (
            <Module
                {...this.props}
                type="transform tree"
                loading={loading}
                empty={empty}
                tooMuchData={tooMuchData}
                className={styles["transform-tree-module"]}
            >
                <div
                    className={styles.content}
                    data-testid="transform-tree-module"
                    data-value={nodes[0]?.url}
                >
                    {this.hasWebglContext && (
                        <ViewerView
                            className={styles.view}
                            unfocused={!focused}
                            transformNodes={nodes}
                            camera={this.camera}
                            startingOrientation="isometric"
                            onChangeCamera={this.onChangeCamera}
                            deviceId={selectedDeviceId}
                            disableUrdf={true}
                        />
                    )}
                    <WebglError visible={!this.hasWebglContext} />
                </div>
                <ModuleFooter visible={hovering} floating={true}>
                    {current && selectedDeviceId && (
                        <DeviceTags
                            deviceId={selectedDeviceId}
                            tags={current.tags}
                            time={current.time}
                        />
                    )}
                    <DeviceSelector
                        selected={selectedDeviceId}
                        deviceIds={deviceIds}
                        onSelect={this.onSelectDevice}
                        fullscreen={fullscreen}
                    />
                </ModuleFooter>
            </Module>
        );
    }

    private onSelectDevice = (deviceId: Uuid) => {
        this.selectedDeviceId = deviceId;
    };

    private onChangeCamera = (camera: ICamera | undefined) => {
        this.changeConfiguration({ camera });
    };

    private changeConfiguration(
        updates: Partial<ITransformTreeViewConfiguration>,
    ) {
        const { streamName, onChangeConfiguration } = this.props;

        if (!onChangeConfiguration) {
            return;
        }

        onChangeConfiguration({
            streamName,
            type: "transform tree",
            updates,
        });
    }
}
